import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { NOTICIA_FILTRO_TODOS, NoticiaCategoria } from '../../models/noticia.model';
import { NoticiaCardComponent } from '../../noticia/noticia-card/noticia-card.component';
import { NoticiasService } from '../../services/noticias.service';

const ITENS_POR_PAGINA = 4;

const ACENTOS = /\p{Diacritic}/gu;

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

@Component({
  selector: 'app-section-noticias',
  templateUrl: './section-noticias.component.html',
  styleUrls: ['./section-noticias.component.css'],
  imports: [RouterLink, LucideModule, NoticiaCardComponent],
})
export class SectionNoticiasComponent {
  /** As 24 mais recentes, numa chamada só: chips, busca e páginas filtram no cliente. */
  private readonly recurso = inject(NoticiasService).homepage();

  readonly carregando = this.recurso.carregando;

  /** As categorias presentes nas notícias carregadas, na ordem em que aparecem. */
  readonly filtros = computed<readonly NoticiaCategoria[]>(() => {
    const vistas = new Map<string, NoticiaCategoria>();

    for (const noticia of this.recurso.noticias()) {
      if (noticia.categoria && !vistas.has(noticia.categoria.id)) {
        vistas.set(noticia.categoria.id, noticia.categoria);
      }
    }

    return [NOTICIA_FILTRO_TODOS, ...vistas.values()];
  });

  readonly categoriaAtiva = signal(NOTICIA_FILTRO_TODOS.id);
  readonly termoBusca = signal('');
  readonly paginaAtual = signal(0);

  readonly noticiasFiltradas = computed(() => {
    const categoria = this.categoriaAtiva();
    const termo = normalizar(this.termoBusca());

    return this.recurso.noticias().filter((noticia) => {
      const combinaCategoria =
        categoria === NOTICIA_FILTRO_TODOS.id || noticia.categoria?.id === categoria;
      const combinaBusca = !termo || normalizar(noticia.titulo).includes(termo);
      return combinaCategoria && combinaBusca;
    });
  });

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.noticiasFiltradas().length / ITENS_POR_PAGINA))
  );

  readonly paginas = computed(() => Array.from({ length: this.totalPaginas() }, (_, i) => i));

  readonly noticiasPagina = computed(() => {
    const inicio = this.paginaAtual() * ITENS_POR_PAGINA;
    return this.noticiasFiltradas().slice(inicio, inicio + ITENS_POR_PAGINA);
  });

  selecionarCategoria(categoriaId: string): void {
    this.categoriaAtiva.set(categoriaId);
    this.paginaAtual.set(0);
  }

  aoDigitarBusca(event: Event): void {
    this.termoBusca.set((event.target as HTMLInputElement).value);
    this.paginaAtual.set(0);
  }

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);
  }
}
